import React, { ComponentProps } from "react";

import EnvironmentConfiguration from "../../../Constants/EnvironmentConfiguration";
import LeadResultType from "../../../Constants/LeadResultType";
import SessionContextValueProvider from "../../../Providers/Value/SessionContextValueProvider";
import UserContextValueProvider from "../../../Providers/Value/UserContextValueProvider";
import { IDispositionConfirmationFormProps } from "../Components/ConfirmationModal/ConfirmationForm";
import SessionDispositionModal from "../Components/ConfirmationModal/SessionDispositionModal";
import { IDispositionStepConfirmationModal } from "../Components/DispositionStep";
import UsePostContactResult from "../Hooks/UsePostContactResult";
import BuildUsePostLeadResultLegacyWrapper from "../Hooks/UsePostLeadResultLegacyWrapper";
import UsePutContactResult from "../Hooks/UsePutContactResult";
import BuildUsePutLeadResultLegacyWrapper from "../Hooks/UsePutLeadResultLegacyWrapper";
import BuildUsePutPostLeadResultLegacyWrapper from "../Hooks/UsePutPostLeadResultLegacyWrapper";
import SessionContextMedicalEscalationConfirmationForm from "./SessionContextMedicalEscalationConfirmationForm";
import SessionContextNotReachedConfirmationForm from "./SessionContextNotReachedConfirmationForm";
import UserContextOtherProceduresConfirmationForm from "./UserContextOtherProceduresConfirmationForm";

/** The confirmation form to be used when the lead result type is specifically paired with a particular form component. */
const ConfirmationFormMap: IDispositionConfirmationFormProps["formMap"] = {
  [EnvironmentConfiguration.leadResultIds[LeadResultType.MedicalEscalation]]:
    SessionContextMedicalEscalationConfirmationForm,
  [EnvironmentConfiguration.leadResultIds[LeadResultType.NotReached]]: SessionContextNotReachedConfirmationForm,
  [EnvironmentConfiguration.leadResultIds[LeadResultType.OtherProcedures]]: UserContextOtherProceduresConfirmationForm
};

const ComposedPostContactResultHook = BuildUsePostLeadResultLegacyWrapper(UsePostContactResult);
const ComposedPutContactResultHook = BuildUsePutLeadResultLegacyWrapper(UsePutContactResult);
const ComposedUsePutPostLeadResultHook = BuildUsePutPostLeadResultLegacyWrapper(
  ComposedPutContactResultHook,
  ComposedPostContactResultHook
);

const ComposedSessionDispositionModal: React.FC<
  Omit<ComponentProps<typeof SessionDispositionModal>, "formMap" | "usePutPostLeadResult">
> = props =>
  SessionDispositionModal({
    ...props,
    formMap: ConfirmationFormMap,
    usePutPostLeadResult: ComposedUsePutPostLeadResultHook
  });

const SessionContextDispositionModal = SessionContextValueProvider({
  Component: ComposedSessionDispositionModal,
  map: ({
    appointment,
    customer,
    ignoreNonCan,
    lead,
    leadDisqualifiers,
    leadResult,
    helpers: { endSession },
    selectedCustomer,
    setLeadResult,
    setCustomerInfo
  }) => ({
    appointment,
    customer,
    disqualificationReasons: leadDisqualifiers,
    ignoreNonCan,
    lead,
    leadResult,
    selectedCustomer,
    endSession,
    setLeadResult,
    setCustomerInfo
  })
});

const UserSessionContextDispositionModal: React.FC<IDispositionStepConfirmationModal> = UserContextValueProvider({
  Component: SessionContextDispositionModal,
  map: ({ leadResultTypes, user: { id }, ivrState }) => ({
    leadResultTypes,
    userId: id,
    ivrState
  })
});

export default UserSessionContextDispositionModal;
